const AVAILABILITY_URL = '/apps/proxy/branch/availability';
const PLACEHOLDER_STREET = '.';

let spinnerTemplate = null;
let unavailableText = '';

// Removes quote glyphs, spacing and case decoration but KEEPS the hospital
// prefix — used for raw substring matching so typing the prefix itself (e.g.
// "ביה") still surfaces `ביה"ח ...` rows.
function stripDecorations(name) {
  return String(name || '')
    .replace(/["'״׳]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Also strips a leading hospital prefix (בית חולים / בית החולים / ביה"ח / בי"ח)
// so free-form delivery_zones entries and canonical `hospitals` names collapse
// to the same core (e.g. "בית החולים סורוקה" and 'ביה"ח סורוקה' → "סורוקה").
// Used for resolving a zone to its metaobject entry (exact core equality).
function normalizeHospitalName(name) {
  return stripDecorations(name).replace(/^(?:בית ה?חולים|בי[ה]?ח) /, '');
}

// Collapses any hospital prefix to one canonical token instead of removing it,
// so search stays a plain substring match: every prefix form of a hospital
// normalizes to the same "ביהח <name>" (cross-matching variants) while the
// prefix is kept, so a partial name like "ביה״ח ל" won't match every city that
// merely contains "ל".
function canonicalizeHospital(name) {
  return stripDecorations(name).replace(/^(?:בית ה?חולים|בי[ה]?ח) /, 'ביהח ');
}

// Resolves each hospital zone to its canonical `hospitals`-metaobject name so
// the row displays and (critically) sends Verifone the exact form it accepts.
// Non-hospital cities have no core match and are left untouched.
function resolveHospitalNames(shippingAreas, hospitals) {
  const hospitalMap = new Map();
  hospitals.forEach((hospital) => {
    const key = normalizeHospitalName(hospital.name);
    if (key) {
      hospitalMap.set(key, hospital);
    }
  });

  shippingAreas.forEach((shippingArea) => {
    const match = hospitalMap.get(normalizeHospitalName(shippingArea.city_name));
    if (match) {
      // delivery_zones are Hebrew-only, so both columns carry the canonical
      // Hebrew name — keeps hospital rows consistent with every other city and
      // findable by Hebrew search in both locales.
      shippingArea.city_name = match.name;
      shippingArea.city_name_eng = match.name;
    }
  });
}

// Builds a regex matching the term against the display text case-insensitively,
// treating quote glyphs as optional (any of "'״׳ or none) — so the highlight
// lands even when the typed quote form differs from the displayed one, and
// includes a quote the user actually typed (e.g. `ביה"` highlights `ביה"`).
function buildHighlightRegex(searchTerm) {
  // Bail unless there's a non-quote char to anchor on, so the pattern can't be
  // all-optional (which would match empty everywhere and loop forever).
  if (!stripDecorations(searchTerm)) {
    return null;
  }

  const optionalQuote = '["\'״׳]?';
  const term = String(searchTerm).replace(/\s+/g, ' ').trim().toLowerCase();

  const pattern = [...term]
    .map((char) => {
      if (/["'״׳]/.test(char)) {
        return optionalQuote;
      }
      if (char === ' ') {
        return '\\s+';
      }
      return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join(optionalQuote);

  return new RegExp(pattern, 'ig');
}

function appendHighlightedText(element, text, searchTerm) {
  element.replaceChildren();

  const regex = buildHighlightRegex(searchTerm);

  if (!regex) {
    element.textContent = text;
    return;
  }

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      element.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
    }

    const mark = document.createElement('mark');
    mark.textContent = match[0];
    element.appendChild(mark);

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    element.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
}

function appendShippingArea(gridElement, shippingArea, isRTL) {
  const cityField = isRTL ? 'city_name' : 'city_name_eng';
  const branchField = isRTL ? 'branch_name' : 'branch_name_eng';

  const city = shippingArea[cityField];
  const branch = shippingArea[branchField];

  const priceCell = createPriceCell();

  gridElement.appendChild(createCell(city));
  gridElement.appendChild(priceCell);
  gridElement.appendChild(createCell(branch));

  fetchRowPrice(shippingArea, priceCell);
}

function createCell(text) {
  const cell = document.createElement('div');
  cell.className = 'boa-shipping-areas-list__cell';
  cell.textContent = text;
  return cell;
}

function createPriceCell() {
  const cell = document.createElement('div');
  cell.className = 'boa-shipping-areas-list__cell';

  if (spinnerTemplate) {
    cell.appendChild(spinnerTemplate.content.cloneNode(true));
  }

  return cell;
}

// Availability rejects *today* past the daily cutoff, so we always ask about the next
// Sunday (never today). See docs.md → Pages → Shipping areas.
function upcomingSundayISODate() {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const israelToday = new Date(`${formatter.format(new Date())}T00:00:00Z`);
  // getUTCDay() on the midnight-UTC stamp above reads the Israeli weekday, 0 = Sunday.
  // No `% 7`: on a Sunday this must land on the *next* one, never today.
  const daysUntilSunday = 7 - israelToday.getUTCDay();

  israelToday.setUTCDate(israelToday.getUTCDate() + daysUntilSunday);

  return israelToday.toISOString().slice(0, 10);
}

async function fetchRowPrice(shippingArea, priceCell) {
  try {
    const response = await fetch(AVAILABILITY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: upcomingSundayISODate(),
        privateAddress: { city: shippingArea.city_name, address1: PLACEHOLDER_STREET },
      }),
    });
    const data = await response.json();

    // Shared formatter (boa-delivery-price.js) so every display site shows agorot the same way.
    const formattedPrice = data?.success && data.isAvailable && data.deliveryPrice
      ? window.BoaDeliveryPrice.format(data.deliveryPrice)
      : '';

    priceCell.textContent = formattedPrice || unavailableText;
  } catch {
    priceCell.textContent = unavailableText;
  }
}

function clearSearchResults(gridElement) {
  const shippingAreaCells = gridElement.querySelectorAll('.boa-shipping-areas-list__cell');

  shippingAreaCells.forEach((cell) => {
    cell.remove();
  });
}

function renderSearchResults(searchResults, gridElement, emptyStateElement, isRTL) {
  clearSearchResults(gridElement);

  if (searchResults.length === 0) {
    gridElement.setAttribute('hidden', true);
    emptyStateElement.removeAttribute('hidden');
    return;
  }

  gridElement.removeAttribute('hidden');
  emptyStateElement.setAttribute('hidden', true);

  searchResults.forEach((shippingArea) => {
    appendShippingArea(gridElement, shippingArea, isRTL);
  });
}

function shippingAreasSearch(searchTerm, shippingAreas, isRTL) {
  const cityField = isRTL ? 'city_name' : 'city_name_eng';
  const term = canonicalizeHospital(searchTerm);

  return shippingAreas.filter((shippingArea) => canonicalizeHospital(shippingArea[cityField]).includes(term));
}

function clearPredictiveResults(resultsListElement) {
  resultsListElement.replaceChildren();
}

function hidePredictiveResults(resultsElement, resultsListElement) {
  resultsElement.setAttribute('hidden', true);
  clearPredictiveResults(resultsListElement);
}

function showPredictiveResults(resultsElement) {
  resultsElement.removeAttribute('hidden');
}

function performSearch(searchTerm, shippingAreas, gridElement, emptyStateElement, isRTL) {
  const results = shippingAreasSearch(searchTerm, shippingAreas, isRTL);
  renderSearchResults(results, gridElement, emptyStateElement, isRTL);
}

function renderPredictiveResults({ predictiveResults, resultsElement, resultsListElement, inputElement, isRTL }) {
  clearPredictiveResults(resultsListElement);

  if (predictiveResults.length === 0) {
    hidePredictiveResults(resultsElement, resultsListElement);
    return;
  }

  const cityField = isRTL ? 'city_name' : 'city_name_eng';
  const searchTerm = inputElement.value.trim();

  predictiveResults.forEach((shippingArea) => {
    const cityName = shippingArea[cityField];
    const button = document.createElement('button');

    button.type = 'button';
    button.className = 'boa-shipping-search__result-item';
    button.setAttribute('role', 'option');
    button.dataset.searchValue = cityName;

    appendHighlightedText(button, cityName, searchTerm);

    resultsListElement.appendChild(button);
  });

  showPredictiveResults(resultsElement);
}

function initShippingAreas() {
  const isRTL = document.documentElement.getAttribute('dir') === 'rtl';

  const shippingAreasDataElement = document.querySelector('[data-shipping-areas-json]');
  const gridElement = document.querySelector('[data-shipping-areas-grid]');
  const emptyStateElement = document.querySelector('[data-shipping-areas-empty-state]');
  const inputElement = document.querySelector('.boa-shipping-search__input');
  const predictiveResultsElement = document.querySelector('.boa-shipping-search__results');
  const predictiveResultsListElement = document.querySelector('.boa-shipping-search__results-list');

  spinnerTemplate = document.querySelector('[data-shipping-spinner]');
  unavailableText = gridElement?.getAttribute('data-price-unavailable') || '';

  if (
    !shippingAreasDataElement ||
    !gridElement ||
    !emptyStateElement ||
    !inputElement ||
    !predictiveResultsElement ||
    !predictiveResultsListElement
  ) {
    console.error('Shipping areas elements were not found', null);
    return;
  }

  let shippingAreas = [];

  try {
    shippingAreas = JSON.parse(shippingAreasDataElement?.textContent?.trim() || '[]');
  } catch (error) {
    console.error('Failed to parse shipping areas data', error);
  }

  const hospitalsDataElement = document.querySelector('[data-hospitals-json]');

  try {
    const hospitals = JSON.parse(hospitalsDataElement?.textContent?.trim() || '[]');
    resolveHospitalNames(shippingAreas, hospitals);
  } catch (error) {
    console.error('Failed to parse hospitals data', error);
  }

  inputElement.addEventListener('input', () => {
    const inputValue = inputElement.value.trim();

    if (!inputValue) {
      hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
      return;
    }

    const predictiveResults = shippingAreasSearch(inputValue, shippingAreas, isRTL);

    renderPredictiveResults({
      predictiveResults,
      resultsElement: predictiveResultsElement,
      resultsListElement: predictiveResultsListElement,
      inputElement,
      isRTL,
    });
  });

  inputElement.addEventListener('focus', () => {
    const inputValue = inputElement.value.trim();

    if (!inputValue) return;

    const predictiveResults = shippingAreasSearch(inputValue, shippingAreas, isRTL);

    renderPredictiveResults({
      predictiveResults,
      resultsElement: predictiveResultsElement,
      resultsListElement: predictiveResultsListElement,
      inputElement,
      isRTL,
    });
  });

  inputElement.addEventListener('keydown', (event) => {
    const resultButtons = Array.from(
      predictiveResultsListElement.querySelectorAll('.boa-shipping-search__result-item')
    );
    const resultsOpen =
      !predictiveResultsElement.hasAttribute('hidden') && resultButtons.length > 0;
    const activeIndex = resultButtons.indexOf(document.activeElement);

    if (event.key === 'ArrowDown' && resultsOpen) {
      event.preventDefault();
      const nextIndex = activeIndex < 0 ? 0 : Math.min(activeIndex + 1, resultButtons.length - 1);
      resultButtons[nextIndex].focus();
      return;
    }

    if (event.key === 'ArrowUp' && resultsOpen) {
      event.preventDefault();
      if (activeIndex <= 0) {
        inputElement.focus();
      } else {
        resultButtons[activeIndex - 1].focus();
      }
      return;
    }

    if (event.key === 'Escape' && resultsOpen) {
      event.preventDefault();
      hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
      inputElement.focus();
      return;
    }

    if (event.key !== 'Enter') return;

    event.preventDefault();

    if (activeIndex >= 0) {
      const cityName = resultButtons[activeIndex].dataset.searchValue || '';
      inputElement.value = cityName;
      hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
      performSearch(cityName, shippingAreas, gridElement, emptyStateElement, isRTL);
      return;
    }

    const searchTerm = inputElement.value.trim();

    hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);

    if (!searchTerm) {
      gridElement.setAttribute('hidden', true);
      emptyStateElement.setAttribute('hidden', true);
      return;
    }

    performSearch(searchTerm, shippingAreas, gridElement, emptyStateElement, isRTL);
  });

  predictiveResultsListElement.addEventListener('keydown', (event) => {
    const button = event.target.closest('.boa-shipping-search__result-item');
    if (!button) return;

    const resultButtons = Array.from(
      predictiveResultsListElement.querySelectorAll('.boa-shipping-search__result-item')
    );
    const activeIndex = resultButtons.indexOf(button);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      resultButtons[Math.min(activeIndex + 1, resultButtons.length - 1)]?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (activeIndex <= 0) {
        inputElement.focus();
      } else {
        resultButtons[activeIndex - 1].focus();
      }
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const cityName = button.dataset.searchValue || '';
      inputElement.value = cityName;
      hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
      performSearch(cityName, shippingAreas, gridElement, emptyStateElement, isRTL);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
      inputElement.focus();
    }
  });

  predictiveResultsListElement.addEventListener('click', (event) => {
    const button = event.target.closest('.boa-shipping-search__result-item');

    if (!button) return;

    const cityName = button.dataset.searchValue || '';

    inputElement.value = cityName;
    hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
    performSearch(cityName, shippingAreas, gridElement, emptyStateElement, isRTL);
  });

  document.addEventListener('click', (event) => {
    const isClickInsideSearch = event.target.closest('.boa-shipping-search');

    if (!isClickInsideSearch) {
      hidePredictiveResults(predictiveResultsElement, predictiveResultsListElement);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingAreas);
} else {
  initShippingAreas();
}
